<script setup lang="ts">
import type { StripePaymentMethodResponse } from '@/types/checkout';
import type { PaymentCard } from '@/types/common';
import { loadStripe } from '@stripe/stripe-js';
import useVuelidate from '@vuelidate/core';
import { helpers, requiredIf } from '@vuelidate/validators';
import appearance from '../../plugins/stripe';

const props = defineProps({
	cards: {
		type: Array as PropType<PaymentCard[]>,
		required: true,
		default: () => [],
	},
	totalAmount: {
		type: Number,
		required: true,
	},
	loading: {
		type: Boolean,
		required: true,
		default: false,
	},
});

const emit = defineEmits<{
	(e: 'onSaveCardChange', isSaveCard: boolean): void;
}
>();

const tabs = [
	{
		title: 'Your saved cards',
		name: PAYMENT_SELECTION_TABS.SAVE_CARDS,
	},
	{
		title: 'Use another card',
		name: PAYMENT_SELECTION_TABS.USE_NEW_CARD,
	},
];

const route = useRoute();
const checkoutStore = useCheckoutStore();
const stripeCardFormInstance = ref();
const stripePaymentInstance = ref();
const stripeElements = ref();
const paymentElement = ref();
const isStripeFormLoaded = ref(false);
const isInitializing = ref(true);
const cardName = ref('');
const isSubmit = ref(false);
const activeTab = ref(tabs[0].name);
const selectedSavedCardOption = ref('');

const rules = {
	cardName: {
		requiredCardNameIf: helpers.withMessage('Please enter the name on your card.', requiredIf(isSubmit)),
	},
};

const v$ = useVuelidate(rules, { cardName });

const currentFullUrl = computed(() => {
	// Accessing the full path of the current route
	return getFullUrlPath(route.fullPath);
});

async function initStripeFormInstance() {
	if (!checkoutStore.stripePublishableKey) {
		throw new TypeError('Stripe public key is not defined');
	}

	if (!checkoutStore.stripeAccountId) {
		throw new TypeError('Stripe account id is not defined');
	}

	// Init credit card form instance. Use the instance to collect saved credit card on platform's account.
	stripeCardFormInstance.value = await loadStripe(checkoutStore.stripePublishableKey);
	if (stripeCardFormInstance.value) {
		stripeElements.value = stripeCardFormInstance.value.elements({
			mode: 'payment',
			amount: toCents(props.totalAmount),
			currency: 'usd',
			paymentMethodCreation: 'manual',
			appearance,
			loader: 'never',
		});
	}

	// Init payment instance with the header Stripe-Account(Organizer account's id)
	stripePaymentInstance.value = await loadStripe(checkoutStore.stripePublishableKey, { stripeAccount: checkoutStore.stripeAccountId});
}

async function initStripeForm() {
	// Init payment form for a new credit card.
	if (stripeCardFormInstance.value) {
		// Payment element
		if (!paymentElement.value) {
			paymentElement.value = stripeElements.value.create('payment', {
				layout: {
					type: 'tabs',
					defaultCollapsed: false,
				},
				wallets: {
					applePay: 'never',
					googlePay: 'never',
				},
			});
		}

		paymentElement.value.mount('#payment-element');
		paymentElement.value.on('ready', async () => {
			// Handle ready event
			isStripeFormLoaded.value = true;
			isInitializing.value = false;
		});
	}
}

function cleanupPaymentForm() {
	cardName.value = '';
	selectedSavedCardOption.value = '';
	isStripeFormLoaded.value = false;
	if (paymentElement.value) {
		paymentElement.value.unmount();
	}
}

async function handleCreateStripePaymentMethod(): Promise<StripePaymentMethodResponse> {
	if (selectedSavedCardOption.value) {
		const cardInfo = props.cards.find((card) => selectedSavedCardOption.value === card.stripePaymentMethodId);
		return {
			savedCardInfo: cardInfo,
		};
	}

	// Trigger card name validation
	isSubmit.value = true;
	v$.value.$touch();

	// Trigger new card to stripe to validate form
	const {error: submitError} = await stripeElements.value.submit();
	if (submitError) {
		return {
			error: submitError,
		};
	}

	// Create new card
	const elements = stripeElements.value;
	const {error, paymentMethod} = await stripeCardFormInstance.value.createPaymentMethod({
		elements,
		params: {
			billing_details: {
				name: cardName.value,
			},
		},
	});

	return {
		error,
		paymentMethod,
		isCardNameValid: !v$.value.$error,
	};
}

async function handleConfirmStripePayment(paymentSecret: string) {
	return await stripePaymentInstance.value.confirmPayment({
		clientSecret: paymentSecret,
		confirmParams: {
			return_url: currentFullUrl.value,
		},
		redirect: 'if_required',
	});
}

function handleSaveCardChange(isSave: boolean) {
	emit('onSaveCardChange', isSave);
}

function handleCardNameChange(value: string) {
	cardName.value = value;
}

watch(activeTab, (newActiveTab) => {
	if (newActiveTab === PAYMENT_SELECTION_TABS.USE_NEW_CARD) {
		// Delay to wait for DOM `id=payment-element` to render.
		setTimeout(() => {
			initStripeForm();
		}, 0);
	} else {
		cleanupPaymentForm();
	}
});

onMounted(async () => {
	await initStripeFormInstance();
	if (!props.cards.length) {
		initStripeForm();
	}
});

onUnmounted(() => {
	cleanupPaymentForm();
});

defineExpose({
	createStripePaymentMethod: handleCreateStripePaymentMethod,
	confirmStripePayment: handleConfirmStripePayment,
});
</script>
<template>
  <div
    v-if="loading"
    class="credit-card-loaders"
  >
    <ButtonSkeletonLoader
      v-for="(_count, index) in 3"
      :key="index"
      height="44"
    />
  </div>
  <template v-else>
    <div
      v-if="cards.length"
      class="save-card-form"
    >
      <VTabs
        v-model="activeTab"
        class="save-card-tabs"
        centered
        grow
        height="32px"
      >
        <VTab
          v-for="tab in tabs"
          :key="tab.name"
          :value="tab.name"
          :ripple="false"
          exact
        >
          {{ tab.title }}
        </VTab>
      </VTabs>
      <VWindow
        v-model="activeTab"
        class="save-card-tabs-content"
      >
        <VWindowItem
          :value="PAYMENT_SELECTION_TABS.SAVE_CARDS"
        >
          <div class="save-card-form-options">
            <BaseRadioButton
              v-for="(card, index) in cards"
              :id="`${card.brand}-${index}`"
              :key="index"
              v-model="selectedSavedCardOption"
              :name="card.brand"
              :value="card.stripePaymentMethodId"
              :disabled="card.isExpired"
            >
              <template #icon>
                <component
                  :is="getCreditCardIcon(card.brand)"
                  class="icon-payment credit-card"
                />
              </template>
              <template #label>
                <CreditCardInfo :card="card" />
              </template>
            </BaseRadioButton>
          </div>
        </VWindowItem>
        <VWindowItem
          :value="PAYMENT_SELECTION_TABS.USE_NEW_CARD"
        >
          <div class="new-card-form-wrapper">
            <CreditCardCreateForm
              v-if="activeTab === PAYMENT_SELECTION_TABS.USE_NEW_CARD"
              :is-stripe-form-loaded="isStripeFormLoaded"
              :loading="isInitializing"
              :error-card-name-message="getValidationErrorMessage(v$.cardName.$errors)"
              is-show-save-for-future
              @on-save-card-change="handleSaveCardChange"
              @on-card-name-change="handleCardNameChange"
            >
              <div
                id="payment-element"
              />
            </CreditCardCreateForm>
          </div>
        </VWindowItem>
      </VWindow>
    </div>
    <CreditCardCreateForm
      v-else
      :is-stripe-form-loaded="isStripeFormLoaded"
      :error-card-name-message="getValidationErrorMessage(v$.cardName.$errors)"
      is-show-save-for-future
      @on-save-card-change="handleSaveCardChange"
      @on-card-name-change="handleCardNameChange"
    >
      <div
        id="payment-element"
      />
    </CreditCardCreateForm>
  </template>
</template>
<style scoped lang="scss">
.save-card-form {
	background: colors-get(gray, 50);
	@include border-radius-lg;

	.save-card-tabs {
		padding-left: spacings-get(3);
		padding-right: spacings-get(3);
		margin-top: spacings-get(3);
	}

	.save-card-tabs-content {
		padding: 0 spacings-get(3) spacings-get(3);
	}

	&-options {
		display: flex;
		flex-direction: column;
		gap: spacings-get(3);
		margin-top: spacings-get(3);
	}

	.new-card-form-wrapper {
		display: flex;
		flex-direction: column;
		gap: spacings-get(4);
		margin-top: spacings-get(4);
	}
}

.credit-card-loaders {
	display: flex;
	flex-direction: column;
	row-gap: spacings-get(4);
	padding: spacings-get(3);
	background-color: colors-get(gray, 50);
	@include border-radius-default;
}
</style>